import { useEffect, useRef, useState } from "react";
import styles from "./issaInsights.module.scss";
import {
  ISSA_BUTTON_PENDING_MARKER,
  ISSA_IMAGE_PENDING_MARKER,
  ISSA_PENDING_MARKERS,
  ISSA_TABLE_PENDING_MARKER,
  advancePastMarkers,
  formatElapsedSeconds,
  parseIssaStatus,
  preprocessAiResponse,
  renderMarkdown,
} from "./issaResponseHelpers.ts";

/** Seconds since `startedAt`, ticking once a second while mounted. */
function useElapsedSeconds(startedAt: number | undefined): number | null {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!startedAt) return;
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [startedAt]);

  if (!startedAt) return null;
  return Math.max(0, Math.floor((now - startedAt) / 1000));
}

/**
 * Typewriter catch-up: reveals `target` a few characters per frame instead of
 * jumping whole polled chunks in at once. When the target no longer extends
 * what is shown (the stream was cleared or rewritten), display jumps to it.
 */
function useTypewriter(target: string): string {
  const [shown, setShown] = useState(target);
  const shownRef = useRef(target);

  useEffect(() => {
    if (target === shownRef.current) return;
    // Reduced motion: no reveal animation, the text simply appears.
    if (!target.startsWith(shownRef.current) || window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) {
      shownRef.current = target;
      setShown(target);
      return;
    }
    const timer = setInterval(() => {
      const current = shownRef.current;
      if (current.length >= target.length) {
        clearInterval(timer);
        return;
      }
      // Adaptive step: a fresh chunk reveals over ~0.4s however large it is.
      const step = Math.max(2, Math.ceil((target.length - current.length) / 12));
      // Pending markers are revealed whole so they never show half-typed.
      const cut = advancePastMarkers(target, current.length + step, ISSA_PENDING_MARKERS);
      const next = target.slice(0, cut);
      shownRef.current = next;
      setShown(next);
    }, 32);
    return () => clearInterval(timer);
  }, [target]);

  return target.startsWith(shown) ? shown : target;
}

/** Swap the server's pending markers for loading skeletons shaped like what
 *  the final answer will carry there: the picture, the "Export table" control,
 *  the action card. The markers survive markdown rendering as HTML comments. */
function withPendingSkeletons(html: string): string {
  const imageSkeleton =
    `<span class="${styles.imagePendingSkeleton}" role="status" aria-label="Preparing the picture"></span>`;
  const tableSkeleton =
    `<span class="${styles.tablePendingSkeleton}" role="status">` +
    `<span class="${styles.pendingSkeletonDot}" aria-hidden="true"></span>` +
    `<span class="${styles.pendingSkeletonLabel}">Preparing table export\u2026</span></span>`;
  const buttonSkeleton =
    `<span class="${styles.buttonPendingSkeleton}" role="status">` +
    `<span class="${styles.pendingSkeletonBadge}" aria-hidden="true"></span>` +
    `<span class="${styles.pendingSkeletonLine}" aria-hidden="true"></span>` +
    `<span class="${styles.pendingSkeletonLineShort}" aria-hidden="true"></span>` +
    `<span class="${styles.pendingSkeletonAction}" aria-hidden="true"></span>` +
    `<span class="${styles.pendingSkeletonLabel}">Preparing action\u2026</span></span>`;
  return html
    .split(ISSA_IMAGE_PENDING_MARKER).join(imageSkeleton)
    .split(ISSA_TABLE_PENDING_MARKER).join(tableSkeleton)
    .split(ISSA_BUTTON_PENDING_MARKER).join(buttonSkeleton);
}

/** The animated canvas shown while an image is being generated: a quiet
 *  landscape composing itself. Motion is deliberately sparse - a slow light
 *  drift, the sun's glow breathing, two twinkling sparks, and a progress
 *  shimmer. The hills are a static composition; SVG shapes animate opacity
 *  only (SVG transforms jitter), the glow is HTML so its scale composites. */
function ImageGenCanvas() {
  return (
    <div className={styles.imageGenFrame} role="img" aria-label="Generating image">
      <svg
        className={styles.imageGenScene}
        viewBox="0 0 160 104"
        preserveAspectRatio="xMidYMid slice"
        aria-hidden="true"
      >
        <circle className={styles.genSun} cx="118" cy="30" r="11" />
        <path
          className={styles.genHillFar}
          d="M0 66 Q30 46 56 58 T104 44 T160 52 L160 104 L0 104 Z"
        />
        <path
          className={styles.genHillBack}
          d="M0 82 Q26 58 54 70 T104 58 T160 68 L160 104 L0 104 Z"
        />
        <path
          className={styles.genHillFront}
          d="M0 96 Q34 74 68 86 T126 78 T160 86 L160 104 L0 104 Z"
        />
        <path
          className={`${styles.genSpark} ${styles.genSparkA}`}
          d="M32 22 l1.6 4.4 L38 28 l-4.4 1.6 L32 34 l-1.6 -4.4 L26 28 l4.4 -1.6 Z"
        />
        <path
          className={`${styles.genSpark} ${styles.genSparkB}`}
          d="M66 14 l1.2 3.3 L70.5 18.5 l-3.3 1.2 L66 23 l-1.2 -3.3 L61.5 18.5 l3.3 -1.2 Z"
        />
      </svg>
      <div className={styles.genSunGlow} aria-hidden="true" />
      <div className={styles.genProgress} aria-hidden="true" />
    </div>
  );
}

/**
 * The inside of Issa's "working" area while a prompt is pending.
 *
 * Once the model starts writing the final answer, the streamed partial text
 * takes over, revealed with a typewriter animation and a blinking caret.
 * Before that, image-generation statuses (marked by the server, see
 * parseIssaStatus) show the animated canvas, and everything else keeps the
 * classic three typing dots with the status text. A plain elapsed timer sits
 * underneath throughout.
 */
export function IssaWorkingStatus({
  statusText,
  fallback,
  partialText,
  startedAt,
  statusInDock = false,
}: {
  statusText: string | null | undefined;
  fallback: string;
  partialText?: string | null;
  startedAt?: number;
  /** The page shows the status line and timer beside Issa in the dock
   *  (IssaDockStatus), so here only the streamed answer and the image canvas
   *  remain. The quick chat leaves this off and keeps everything inline. */
  statusInDock?: boolean;
}) {
  const elapsedSeconds = useElapsedSeconds(startedAt);
  const typedText = useTypewriter(partialText ?? "");

  // Keeping this in view is the chat's job (useScrollFollow), which follows it
  // only while the reader is at the end.
  const elapsedRow = !statusInDock && elapsedSeconds !== null && (
    <div className={styles.elapsedRow}>{formatElapsedSeconds(elapsedSeconds)}</div>
  );

  if (typedText) {
    // The server already buffers and tag-strips the stream; the client-side
    // preprocess is defense in depth against any tag that slips through.
    // Pending markers become loading skeletons where the picture, table export
    // or action card will land once the final answer arrives.
    const html = withPendingSkeletons(
      renderMarkdown(preprocessAiResponse(typedText).displayText),
    );
    return (
      <>
        <div className={styles.streamingAnswer}>
          <span dangerouslySetInnerHTML={{ __html: html }} />
          <span className={styles.streamCaret} aria-hidden="true" />
        </div>
        {elapsedRow}
      </>
    );
  }

  const parsed = parseIssaStatus(statusText);

  if (parsed?.kind === "image") {
    return (
      <>
        <div className={styles.imageGenWrap}>
          <ImageGenCanvas />
          {!statusInDock && <div key={parsed.text} className={styles.statusBlock}>{parsed.text}</div>}
        </div>
        {elapsedRow}
      </>
    );
  }

  if (statusInDock) return null;

  // Same pulsing dots as the dock bubble, after the status text.
  return (
    <>
      <div className={styles.workingStatus} role="status">
        <div key={parsed?.text ?? fallback} className={styles.statusBlock}>
          {parsed?.text ?? fallback}
        </div>
        <span className={styles.issaDockDots} aria-hidden="true">
          <i /><i /><i />
        </span>
      </div>
      {elapsedRow}
    </>
  );
}

/** Issa's reasoning line beside the dock figure while a prompt runs: the
 *  server's live status (image statuses without their marker) and the timer. */
export function IssaDockStatus({
  statusText,
  fallback,
  startedAt,
  thinking,
}: {
  statusText: string | null | undefined;
  fallback: string;
  startedAt?: number;
  /** Dots only before the answer starts streaming, as in the animation template. */
  thinking: boolean;
}) {
  const elapsedSeconds = useElapsedSeconds(startedAt);
  const text = parseIssaStatus(statusText)?.text ?? fallback;
  return (
    <span className={styles.issaDockBubble} role="status">
      <span key={text} className={styles.issaDockText}>{text}</span>
      {thinking && (
        <span className={styles.issaDockDots} aria-hidden="true">
          <i /><i /><i />
        </span>
      )}
      {elapsedSeconds !== null && (
        <span className={styles.issaDockElapsed}>{formatElapsedSeconds(elapsedSeconds)}</span>
      )}
    </span>
  );
}

/** Over the bottom of the chat while the reader is scrolled up and the newest
 *  message is out of sight below - the answer still coming, or one that came
 *  while they read. The chat never scrolls them away; this takes them down. */
export function IssaJumpToLatest({
  answering,
  assistantName,
  onClick,
}: {
  answering: boolean;
  assistantName: string;
  onClick: () => void;
}) {
  const label = answering ? `${assistantName} is answering` : "New answer";
  return (
    <button
      type="button"
      className={styles.jumpToLatest}
      onClick={onClick}
      aria-label={`${label}, scroll down to it`}
    >
      <span>{label}</span>
      {answering && (
        <span className={styles.issaDockDots} aria-hidden="true">
          <i /><i /><i />
        </span>
      )}
      <svg viewBox="0 0 16 16" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M8 3v10M3.5 8.5 8 13l4.5-4.5" />
      </svg>
    </button>
  );
}
